import { useState } from 'react';
import { Icon, type IconName } from './Icon';

export interface NewMenuItem {
  icon: IconName;
  title: string;
  sub?: string;
  onClick: () => void;
  visible?: boolean;
  /** Voce evidenziata (es. contestuale alla route corrente) */
  highlight?: boolean;
}
export interface NewMenuGroup {
  label: string;
  items: NewMenuItem[];
}

/**
 * Menu di creazione condiviso (pattern P2): bottone primario "+ Nuovo ▾" con
 * voci a gruppi in stile icon-tile + titolo + sottotitolo. Le voci con
 * visible=false spariscono; i gruppi vuoti non vengono renderizzati.
 */
export function NewMenu({
  groups,
  label = 'Nuovo',
  size,
  aurora,
}: {
  groups: NewMenuGroup[];
  label?: string;
  size?: 'sm';
  /** Accento aurora (riservato al CTA chiave, es. il "+ Nuovo" globale) */
  aurora?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const visibleGroups = groups
    .map((g) => ({ ...g, items: g.items.filter((it) => it.visible !== false) }))
    .filter((g) => g.items.length > 0);
  if (!visibleGroups.length) return null;

  return (
    <div className="relative new-menu">
      <button className={`btn ${aurora ? 'btn-aurora' : 'btn-primary'} ${size === 'sm' ? 'btn-sm' : ''}`} onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}>
        <Icon name="plus" size={12} /> {label} <Icon name="chevronD" size={10} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60] new-menu-backdrop" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-[61] border border-line bg-white shadow-lg overflow-hidden new-menu-pop" style={{ minWidth: 340, borderRadius: 'var(--r-card)' }} role="menu">
            {visibleGroups.map((g, gi) => (
              <div key={g.label} className={`p-2 ${gi < visibleGroups.length - 1 ? 'border-b border-line' : ''}`}>
                <div className="t-micro mb-1.5">{g.label}</div>
                {g.items.map((it) => (
                  <div
                    key={it.title}
                    className="flex items-center gap-2.5 p-2.5 hover:bg-paper-2 cursor-pointer transition new-menu-row"
                    role="menuitem"
                    style={{ borderRadius: 'var(--r-ctl)', ...(it.highlight ? { background: 'color-mix(in oklab, var(--accent) 7%, transparent)' } : {}) }}
                    onClick={() => { setOpen(false); it.onClick(); }}
                  >
                    <div
                      className="w-9 h-9 grid place-items-center"
                      style={it.highlight
                        ? { borderRadius: 'var(--r-tile)', background: 'color-mix(in oklab, var(--accent) var(--tint-bg), transparent)', color: 'var(--accent)', border: '1px solid color-mix(in oklab, var(--accent) var(--tint-border), transparent)' }
                        : { borderRadius: 'var(--r-tile)', background: 'var(--paper-2)', color: 'var(--text)' }}
                    >
                      <Icon name={it.icon} size={16} />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold">{it.title}</div>
                      {it.sub && <div className="mute text-[12px]">{it.sub}</div>}
                    </div>
                    <Icon name="arrowRight" size={12} className="mute" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
